import {
  FunctionComponent,
  memo,
  ReactNode,
  SVGProps,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router';
import classes from 'classnames';
import Heading from '@/components/Heading/Heading';
import Paragraph from '@/components/Paragraph/Paragraph';
import Placeholder from '@/components/Placeholder/Placeholder';
import Touchable from '@/components/Touchable/Touchable';
import ChevronIcon from '@/assets/icons/chevron-sharp.svg?react';
import styles from './MenuButton.module.scss';

export type Props = {
  heading?: string;
  info?: string;
  Icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  url?: string;
  className?: string;
  dataTest?: string;
  loading?: boolean;
  label?: string;
  additionalInfo?: ReactNode;
  onMouseEnter?: () => void;
};

const MenuButtonMemo = memo(function MenuButton({
  heading,
  info,
  Icon,
  url,
  className,
  dataTest,
  loading,
  label,
  additionalInfo,
  onMouseEnter,
}: Props) {
  const navigate = useNavigate();
  const handleNavigate = useCallback(() => {
    if (url) {
      navigate(url);
    }
  }, [url, navigate]);

  return (
    <div className={classes(styles.container, className)} data-test={dataTest}>
      <Touchable
        dataTest="button"
        className={styles['button-container']}
        buttonClassName={styles.button}
        onClick={handleNavigate}
        onMouseEnter={onMouseEnter}
      >
        <div className={styles.description}>
          {Icon && <Icon className={styles.icon} data-test="menuBtn-icon" />}
          <div>
            <Heading dataTest="heading" className={styles.heading}>
              <Placeholder loading={loading} dataTest="menuBtn-heading">
                {heading}
              </Placeholder>
            </Heading>
            <Paragraph
              dataTest="subheading"
              type="small"
              className={styles.paragraph}
            >
              <Placeholder loading={loading} dataTest="menuBtn-info">
                {info}
              </Placeholder>
            </Paragraph>
            <Paragraph dataTest="text" type="small">
              {additionalInfo}
            </Paragraph>
          </div>
        </div>
        {label && (
          <div className={styles.label} data-test="menuBtn-label">
            {label}
          </div>
        )}
        <ChevronIcon
          className={styles.chevron}
          data-test="menuBtn-chevron-icon"
        />
      </Touchable>
    </div>
  );
});

export default MenuButtonMemo;
